import { AlertCircle, CheckCircle, ChevronDown, Info } from 'lucide-react';
import React, { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  formatPhoneNumber,
  getCountryCodes,
  getCountryFlag,
  getPhonePlaceholder,
  getValidationRule,
  validatePhoneNumber,
} from '../../utils/phoneValidation';

interface CountryCode {
  code: string;
  country: string;
  iso2?: string;
  flag?: string;
}

interface PhoneInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type' | 'onChange'> {
  label?: string;
  error?: string;
  countryCodes?: CountryCode[];
  selectedCountryCode?: string;
  onCountryCodeChange?: (code: string) => void;
  defaultCountryCode?: string;
  onChange?: (value: string) => void;
}

/**
 * Strip leading country dialing-code digits from a raw number so it fits the
 * expected national-number length. For example, if the rule expects 10 digits
 * (US) and the stored value is `15551234567` (11 digits), we strip the leading
 * `1` to get `5551234567`.
 */
const normalizeToNationalNumber = (digits: string, countryCode: string): string => {
  const rule = getValidationRule(countryCode);
  if (!rule) return digits;

  // Already within valid range — nothing to strip
  if (digits.length >= rule.minLength && digits.length <= rule.maxLength) return digits;

  // Value is longer than maxLength — try stripping the dialing-code prefix
  const dialDigits = countryCode.replace(/\D/g, ''); // e.g. "+1" → "1", "+91" → "91"
  if (digits.length > rule.maxLength && digits.startsWith(dialDigits)) {
    const stripped = digits.slice(dialDigits.length);
    if (stripped.length >= rule.minLength && stripped.length <= rule.maxLength) {
      return stripped;
    }
  }

  return digits;
};

const PhoneInput = forwardRef<HTMLInputElement, PhoneInputProps>(
  (
    {
      label,
      error,
      countryCodes: externalCountryCodes,
      selectedCountryCode: externalSelectedCode,
      onCountryCodeChange: externalOnCodeChange,
      defaultCountryCode = '+1',
      onChange,
      className = '',
      disabled,
      required,
      ...props
    },
    ref
  ) => {
    // Self-contained country code state (used when external props are not provided)
    const [internalCountryCode, setInternalCountryCode] = useState(defaultCountryCode);

    // Controlled mode = external callback provided (signals intent to manage state externally)
    const isControlled = externalOnCodeChange !== undefined;
    const selectedCountryCode = isControlled
      ? (externalSelectedCode ?? defaultCountryCode)
      : internalCountryCode;
    const countryCodes = externalCountryCodes ?? getCountryCodes();

    const handleCountryCodeChange = useCallback(
      (code: string) => {
        if (externalOnCodeChange) {
          externalOnCodeChange(code);
        }
        if (!isControlled) {
          setInternalCountryCode(code);
        }
      },
      [externalOnCodeChange, isControlled]
    );

    const [showCountryDropdown, setShowCountryDropdown] = useState(false);
    const [inputValue, setInputValue] = useState('');
    const [showFormatInfo, setShowFormatInfo] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });
    const triggerRef = useRef<HTMLDivElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const inputId = useRef(`phone-input-${Math.random().toString(36).slice(2, 9)}`).current;
    const validationRule = getValidationRule(selectedCountryCode);

    // Get flag for the selected country
    const selectedCountry = countryCodes.find(c => c.code === selectedCountryCode);
    const selectedFlag =
      selectedCountry?.flag ??
      (selectedCountry?.iso2 ? getCountryFlag(selectedCountry.iso2) : '');

    // Filter countries based on search query
    const filteredCountries = countryCodes.filter(
      country =>
        country.code.toLowerCase().includes(searchQuery.toLowerCase()) ||
        country.country.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (country.iso2 && country.iso2.toLowerCase().includes(searchQuery.toLowerCase()))
    );

    // Update dropdown position (used on open, scroll, resize)
    const updateDropdownPosition = useCallback(() => {
      if (!triggerRef.current) return;
      const rect = triggerRef.current.getBoundingClientRect();
      setDropdownPos({ top: rect.bottom + 4, left: rect.left });
    }, []);

    // Reposition on scroll/resize while dropdown is open
    useEffect(() => {
      if (!showCountryDropdown) return;
      updateDropdownPosition();
      window.addEventListener('scroll', updateDropdownPosition, true);
      window.addEventListener('resize', updateDropdownPosition);
      return () => {
        window.removeEventListener('scroll', updateDropdownPosition, true);
        window.removeEventListener('resize', updateDropdownPosition);
      };
    }, [showCountryDropdown, updateDropdownPosition]);

    // Close dropdown on outside click
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        const target = event.target as Node;
        if (
          triggerRef.current &&
          !triggerRef.current.contains(target) &&
          dropdownRef.current &&
          !dropdownRef.current.contains(target)
        ) {
          setShowCountryDropdown(false);
          setShowFormatInfo(false);
          setSearchQuery('');
          setHighlightedIndex(-1);
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, []);

    // Sync input value with external value prop (handles edit mode)
    useEffect(() => {
      if (props.value != null && props.value !== '') {
        const cleanValue = props.value.toString().replace(/\D/g, '');
        const normalized = normalizeToNationalNumber(cleanValue, selectedCountryCode);
        const formattedValue = formatPhoneNumber(normalized, selectedCountryCode);
        setInputValue(formattedValue);
      } else {
        setInputValue('');
      }
    }, [props.value, selectedCountryCode]);

    const handleCountrySelect = (code: string) => {
      handleCountryCodeChange(code);
      setShowCountryDropdown(false);
      setSearchQuery('');
      setShowFormatInfo(false);
      setHighlightedIndex(-1);
      setInputValue('');
      if (onChange) {
        onChange('');
      }
    };

    const handleDropdownToggle = () => {
      if (disabled) return;
      const newState = !showCountryDropdown;
      setShowCountryDropdown(newState);
      setSearchQuery('');
      setShowFormatInfo(false);
      setHighlightedIndex(-1);
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      const cleanValue = value.replace(/\D/g, '');

      // Enforce maxLength — truncate rather than reject (supports paste)
      const rule = getValidationRule(selectedCountryCode);
      const trimmed = rule ? cleanValue.slice(0, rule.maxLength) : cleanValue;

      const formattedValue = formatPhoneNumber(trimmed, selectedCountryCode);
      setInputValue(formattedValue);

      if (onChange) {
        onChange(trimmed);
      }

      // Trigger native change event for React Hook Form
      if (ref && 'current' in ref && ref.current) {
        ref.current.value = trimmed;
        const event = new Event('change', { bubbles: true });
        ref.current.dispatchEvent(event);
      }

      if (showFormatInfo) {
        setShowFormatInfo(false);
      }
    };

    // Arrow key navigation in dropdown
    const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Escape') {
        setShowCountryDropdown(false);
        setSearchQuery('');
        setHighlightedIndex(-1);
        return;
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setHighlightedIndex(prev =>
          prev < filteredCountries.length - 1 ? prev + 1 : 0
        );
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setHighlightedIndex(prev =>
          prev > 0 ? prev - 1 : filteredCountries.length - 1
        );
        return;
      }
      if (e.key === 'Enter') {
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < filteredCountries.length) {
          handleCountrySelect(filteredCountries[highlightedIndex].code);
        } else if (filteredCountries.length === 1) {
          handleCountrySelect(filteredCountries[0].code);
        }
      }
    };

    // Scroll highlighted item into view
    useEffect(() => {
      if (highlightedIndex < 0 || !dropdownRef.current) return;
      const listContainer = dropdownRef.current.querySelector('[role="listbox"]');
      const items = listContainer?.querySelectorAll('[role="option"]');
      if (items && items[highlightedIndex]) {
        items[highlightedIndex].scrollIntoView({ block: 'nearest' });
      }
    }, [highlightedIndex]);

    // Reset highlight when search changes
    useEffect(() => {
      setHighlightedIndex(-1);
    }, [searchQuery]);

    const isValidPhone =
      inputValue &&
      validatePhoneNumber(inputValue.replace(/\D/g, ''), selectedCountryCode).isValid;

    return (
      <div className="w-full">
        {label && (
          <label htmlFor={inputId} className="block text-sm font-medium text-gray-700 mb-2">
            {label}
            {required && <span className="text-red-500 ml-0.5">*</span>}
          </label>
        )}
        <div className="relative">
          <div
            className={`flex rounded-lg border overflow-hidden transition-all ${
              error
                ? 'border-red-300 focus-within:ring-2 focus-within:ring-red-500 focus-within:border-red-500'
                : 'border-gray-300 focus-within:ring-2 focus-within:ring-brand-primary focus-within:border-brand-primary'
            } ${disabled ? 'bg-gray-100 opacity-60' : ''}`}
          >
            <div className="relative" ref={triggerRef}>
              <button
                type="button"
                onClick={e => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleDropdownToggle();
                }}
                disabled={disabled}
                aria-expanded={showCountryDropdown}
                aria-haspopup="listbox"
                aria-label={`Country code: ${selectedCountryCode}`}
                className={`flex items-center px-3 py-3 bg-gray-50 hover:bg-gray-100 focus:bg-gray-100 outline-none transition-colors border-r border-gray-300 min-w-[100px] cursor-pointer ${showCountryDropdown ? 'bg-gray-100' : ''} ${disabled ? 'cursor-not-allowed hover:bg-gray-50' : ''}`}
              >
                {selectedFlag && (
                  <span className="text-base mr-1.5 pointer-events-none">{selectedFlag}</span>
                )}
                <span className="text-sm font-medium text-gray-700 pointer-events-none">
                  {selectedCountryCode}
                </span>
                <ChevronDown
                  size={16}
                  className={`ml-1 text-gray-500 transition-transform pointer-events-none ${showCountryDropdown ? 'rotate-180' : ''}`}
                />
              </button>
            </div>
            <div className="flex-1 relative">
              <input
                ref={ref}
                id={inputId}
                type="tel"
                value={inputValue}
                onChange={handleInputChange}
                onFocus={() => {
                  if (showCountryDropdown) {
                    setShowCountryDropdown(false);
                    setSearchQuery('');
                    setHighlightedIndex(-1);
                  }
                }}
                placeholder={getPhonePlaceholder(selectedCountryCode)}
                disabled={disabled}
                className={`w-full px-4 py-3 outline-none placeholder-gray-400 text-gray-900 ${
                  validationRule && isValidPhone
                    ? 'pr-16'
                    : validationRule || isValidPhone
                      ? 'pr-12'
                      : 'pr-4'
                } ${disabled ? 'bg-gray-100 cursor-not-allowed' : ''} ${className}`}
                {...props}
              />
              <div className="absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center space-x-1">
                {isValidPhone && !error && <CheckCircle size={16} className="text-green-500" />}
                {validationRule && !disabled && (
                  <button
                    type="button"
                    onClick={() => setShowFormatInfo(!showFormatInfo)}
                    className="text-gray-400 hover:text-brand-primary transition-colors"
                    title="Phone format information"
                  >
                    <Info size={16} />
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Country Dropdown — Portal-based */}
          {showCountryDropdown &&
            createPortal(
              <div
                ref={dropdownRef}
                className="bg-white border border-gray-200 rounded-lg shadow-xl min-w-[300px] max-h-80 z-[9999]"
                style={{
                  position: 'fixed',
                  top: dropdownPos.top,
                  left: dropdownPos.left,
                }}
              >
                <div className="p-3 border-b border-gray-100">
                  <input
                    type="text"
                    placeholder="Search countries..."
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    onKeyDown={handleSearchKeyDown}
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-brand-primary"
                    aria-label="Search countries"
                    aria-controls="phone-country-listbox"
                    aria-activedescendant={
                      highlightedIndex >= 0
                        ? `phone-country-option-${highlightedIndex}`
                        : undefined
                    }
                    autoFocus
                  />
                </div>
                <div
                  id="phone-country-listbox"
                  role="listbox"
                  aria-label="Country codes"
                  className="max-h-60 overflow-y-auto"
                >
                  {filteredCountries.length > 0 ? (
                    filteredCountries.map((item, index) => {
                      const flag =
                        item.flag ?? (item.iso2 ? getCountryFlag(item.iso2) : '');
                      const isHighlighted = index === highlightedIndex;
                      const isSelected = selectedCountryCode === item.code;
                      return (
                        <button
                          key={`${item.code}-${item.iso2 ?? index}`}
                          id={`phone-country-option-${index}`}
                          type="button"
                          role="option"
                          aria-selected={isSelected}
                          onMouseDown={e => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleCountrySelect(item.code);
                          }}
                          onMouseEnter={() => setHighlightedIndex(index)}
                          className={`w-full px-4 py-3 text-left text-sm hover:bg-red-50 transition-colors flex items-center cursor-pointer ${
                            isSelected
                              ? 'bg-red-50 text-brand-primary font-medium'
                              : isHighlighted
                                ? 'bg-gray-100'
                                : 'text-gray-700'
                          }`}
                        >
                          {flag && (
                            <span className="text-base mr-2 pointer-events-none">{flag}</span>
                          )}
                          <span className="font-medium pointer-events-none">{item.code}</span>
                          <span className="text-xs text-gray-500 ml-2 truncate max-w-[200px] pointer-events-none">
                            {item.country}
                          </span>
                        </button>
                      );
                    })
                  ) : (
                    <div className="px-4 py-3 text-sm text-gray-500 text-center">
                      No countries found
                    </div>
                  )}
                </div>
              </div>,
              document.body
            )}

          {/* Format Info Tooltip */}
          {showFormatInfo && validationRule && (
            <div className="absolute top-full right-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-xl p-4 z-50 min-w-[240px]">
              <div className="text-sm">
                <div className="font-semibold text-gray-800 mb-2">
                  Format for {validationRule.country}
                </div>
                <div className="text-gray-600 mb-2">
                  <span className="font-medium">Example:</span> {validationRule.example}
                </div>
                <div className="text-gray-600">
                  <span className="font-medium">Length:</span> {validationRule.minLength}
                  {validationRule.minLength !== validationRule.maxLength &&
                    `-${validationRule.maxLength}`}{' '}
                  digits
                </div>
              </div>
            </div>
          )}
        </div>
        {error && (
          <p className="mt-2 text-sm text-red-600 flex items-center">
            <AlertCircle size={16} className="mr-1" />
            {error}
          </p>
        )}
      </div>
    );
  }
);

PhoneInput.displayName = 'PhoneInput';

export default PhoneInput;
